import React from 'react';
import { string, func, bool, oneOfType } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import IconHeartFilled from '../IconHeartFilled/IconHeartFilled';
import IconHeart from '../IconHeart/IconHeart';

import { getWishlist, isArrayLength } from '../../util/dataExtractor';
import { useDispatch, useSelector } from 'react-redux';
import { currentUserSelector } from '../../ducks/user.duck';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

import { AspectRatioWrapper, AvatarSmall, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import { WISHLIST_PAGE } from '../../util/constants';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    onToggleWishlistModal = () => {},
    page,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;

  const currentUser = useSelector(currentUserSelector);
  const dispatch = useDispatch();

  const wishlistItems = getWishlist(currentUser);
  const combinedWishlistItems = isArrayLength(wishlistItems) && wishlistItems.flatMap(w => w.ids);
  const isAlreadyWishlisted =
    isArrayLength(combinedWishlistItems) &&
    combinedWishlistItems.find(id => id === currentListing?.id?.uuid);

  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const onRemoveWishlistedItem = itemId => {
    // Find the index of the wishlist that contains the item ID
    const wishlistIndex = wishlistItems.findIndex(wishlist => wishlist?.ids?.includes(itemId));
    if (wishlistIndex === -1) {
      console.error('Item not found in any wishlist');
      return; // Early return if the item ID is not found
    }
    // Create a new array without the item ID
    const updatedIds = wishlistItems[wishlistIndex].ids.filter(id => id !== itemId);
    // Avoid mutation, create a new array for wishlistItems
    const updatedWishlistItems = wishlistItems.map((item, index) =>
      index === wishlistIndex ? { ...item, ids: updatedIds } : item
    );
    // Dispatch the updated wishlist using a structured action object
    dispatch(
      updateProfile({
        publicData: {
          wishlist: updatedWishlistItems,
        },
      })
    );
  };

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      {showAuthorInfo ? (
        <div className={css.authorInfo}>
          <AvatarSmall user={author} />{' '}
          <FormattedMessage id="ListingCard.author" values={{ authorName }} />
        </div>
      ) : null}
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
        {/* {page != WISHLIST_PAGE?.LISTING_PAGE && ( */}
        <div
          className={css.favorite}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            if (isAlreadyWishlisted) {
              onRemoveWishlistedItem(id);
            } else {
              onToggleWishlistModal(id);
            }
          }}
        >
          {isAlreadyWishlisted ? <IconHeartFilled /> : <IconHeart />}
        </div>
        {/* )} */}
      </AspectRatioWrapper>
      <div className={css.info}>
        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
